/**
 * NoAccess — the shell-level page for a signed-in principal the reseller gate refuses
 * (H §4.4): any token that is not a `journix-reseller` / `RESELLER` token naming its reseller
 * (a tenant-staff token, a customer token). Rendered by `AuthGate` in place of the `<Outlet/>`,
 * so no nav or screen content ever paints for this principal. Unbranded, like the other
 * boot-level screens.
 *
 * **Sign in again** clears the session (best-effort `logout`, then memory) and sends the
 * browser to centralized login — so a different account can sign in.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SkipLink } from '@/components/SkipLink';
import { authApi } from '@/api/auth';
import { useAuth } from '@/auth/authHooks';
import { redirectToCentralizedLogin } from '@/auth/silentRefresh';
import { HOME_PATH } from '@/routes/home';
import { blankSlateStyle } from './blankSlateStyle';

export function NoAccess() {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  async function handleSignInAgain() {
    setSigningOut(true);
    try {
      await authApi.logout();
    } catch {
      // A failed network logout must not block clearing the local session.
    }
    signOut();
    redirectToCentralizedLogin(HOME_PATH);
  }

  return (
    <>
      <SkipLink />
      <main id="main" tabIndex={-1} style={blankSlateStyle}>
        <h1>{t('auth.noAccessTitle')}</h1>
        <p style={{ maxWidth: '32rem' }}>{t('auth.noAccessBody')}</p>
        <button type="button" onClick={() => void handleSignInAgain()} disabled={signingOut}>
          {signingOut ? t('auth.signingOut') : t('auth.signInAgain')}
        </button>
      </main>
    </>
  );
}
